import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faPlus, faTimes, faShieldHalved, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import {
  WHITELIST_PROJECT_RULE_REMOVAL_ENABLED,
  type WhitelistNewEntry,
  type WhitelistRulePayload
} from '../../shared/whitelistRules.utils';
import { getDomainDisplayName } from '@app/shared/utils/domainDisplay.utils';
import GovernanceTabLoadingState from '../shared/GovernanceTabLoadingState';

export interface WhitelistRulesTabProps {
  domain: string;
  rules: Record<string, WhitelistRulePayload> | null;
  isLoading: boolean;
  selectedRules: string[];
  newEntries: WhitelistNewEntry[];
  canManage: boolean;
  canSelect: boolean;
  canSave: boolean;
  isSaving: boolean;
  hasUnsavedChanges?: boolean;
  onAddCatalogRule: (ruleKey: string) => void;
  onRemoveProjectRule: (ruleKey: string) => void;
  onAddNewEntry: () => void;
  onRemoveNewEntry: (index: number) => void;
  onNewEntryKeyChange: (index: number, value: string) => void;
  onAddRegulatoryTag: (index: number, value: string) => void;
  onRemoveRegulatoryTag: (index: number, tagIndex: number) => void;
  onNewEntryJustificationChange: (index: number, value: string) => void;
  onSave: () => void;
  onReset: () => void;
  onRefresh?: () => void;
}

const humanizeKey = (key: string): string => key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

interface TagInputProps {
  tags: string[];
  onAddTag: (value: string) => void;
  onRemoveTag: (index: number) => void;
  placeholder: string;
  disabled?: boolean;
}

const TagInput = ({ tags, onAddTag, onRemoveTag, placeholder, disabled = false }: TagInputProps) => {
  const [inputValue, setInputValue] = useState('');

  const commitInput = (raw: string) => {
    const trimmed = raw.trim();
    if (trimmed) {
      onAddTag(trimmed);
      setInputValue('');
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return;
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      commitInput(inputValue);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const value = event.target.value;
    if (value.includes(',')) {
      value
        .split(',')
        .map(tag => tag.trim())
        .filter(Boolean)
        .forEach(tag => onAddTag(tag));
      setInputValue('');
    } else {
      setInputValue(value);
    }
  };

  return (
    <Box>
      {tags.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
          {tags.map((tag, index) => (
            <Chip
              key={`${tag}-${index}`}
              label={tag}
              size="small"
              onDelete={disabled ? undefined : () => onRemoveTag(index)}
            />
          ))}
        </Box>
      )}
      <TextField
        fullWidth
        size="small"
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
      />
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
        Press Enter or comma to add
      </Typography>
    </Box>
  );
};

const CatalogRuleDetails = ({
  regulatoryBody,
  justification,
  setA,
  setB
}: {
  regulatoryBody: string[];
  justification: string;
  setA: string[];
  setB: string[];
}) => (
  <>
    {regulatoryBody.length > 0 && (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: setA.length || setB.length ? 1 : 0 }}>
        {regulatoryBody.map(body => (
          <Chip key={body} label={body} size="small" color="primary" variant="outlined" />
        ))}
      </Box>
    )}
    {(setA.length > 0 || setB.length > 0) && (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
        {setA.length > 0 && (
          <Box>
            <Typography variant="caption" color="primary" sx={{ fontWeight: 700 }}>
              Set A
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {setA.map(term => (
                <Chip key={term} label={term} size="small" variant="outlined" />
              ))}
            </Box>
          </Box>
        )}
        {setB.length > 0 && (
          <Box>
            <Typography variant="caption" color="info.main" sx={{ fontWeight: 700 }}>
              Set B
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {setB.map(term => (
                <Chip key={term} label={term} size="small" variant="outlined" color="info" />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    )}
    {justification && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {justification}
      </Typography>
    )}
  </>
);

const WhitelistRulesTab = ({
  domain,
  rules,
  isLoading,
  selectedRules,
  newEntries,
  canManage,
  canSave,
  isSaving,
  hasUnsavedChanges = false,
  onAddCatalogRule,
  onRemoveProjectRule,
  onAddNewEntry,
  onRemoveNewEntry,
  onNewEntryKeyChange,
  onAddRegulatoryTag,
  onRemoveRegulatoryTag,
  onNewEntryJustificationChange,
  onSave,
  onReset,
  onRefresh
}: WhitelistRulesTabProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const hasDomain = domain !== 'N/A';
  const domainLabel = hasDomain ? getDomainDisplayName(domain) : 'this project';

  const catalogEntries = useMemo(() => {
    if (!rules) return [];
    return Object.entries(rules)
      .map(([key, value]) => ({
        key,
        label: humanizeKey(key),
        regulatoryBody: value.regulatory_body ?? [],
        justification: value.justification ?? '',
        setA: value.setA ?? [],
        setB: value.setB ?? []
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rules]);

  const projectRules = useMemo(
    () =>
      selectedRules
        .map(key => catalogEntries.find(entry => entry.key === key))
        .filter((entry): entry is NonNullable<typeof entry> => entry != null),
    [selectedRules, catalogEntries]
  );

  const availableCatalogEntries = useMemo(
    () => catalogEntries.filter(entry => !selectedRules.includes(entry.key)),
    [catalogEntries, selectedRules]
  );

  const filteredAvailableEntries = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return availableCatalogEntries;
    return availableCatalogEntries.filter(entry => {
      const haystack = [
        entry.key,
        entry.label,
        entry.justification,
        ...entry.regulatoryBody,
        ...entry.setA,
        ...entry.setB
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [availableCatalogEntries, searchQuery]);

  const validNewEntryCount = newEntries.filter(entry => entry.key.trim()).length;

  if (isLoading) {
    return <GovernanceTabLoadingState label="Loading whitelist rules..." />;
  }

  if (!hasDomain) {
    return (
      <Box sx={{ textAlign: 'center', py: 5, px: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
        <FontAwesomeIcon icon={faShieldHalved} className="text-muted mb-3" size="2x" />
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Domain required
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: 'auto' }}>
          Set a project domain in project details to load whitelist rules for your industry.
        </Typography>
      </Box>
    );
  }

  const showCatalog = catalogEntries.length > 0;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Whitelist rules available for{' '}
        <Box component="span" sx={{ fontWeight: 700, color: 'primary.main' }}>
          {domainLabel}
        </Box>
      </Typography>

      {!canManage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have read-only access to whitelist settings. Contact an administrator to request changes.
        </Alert>
      )}

      {(projectRules.length > 0 || validNewEntryCount > 0) && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'rgba(13, 110, 253, 0.03)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Project whitelist ({projectRules.length + validNewEntryCount})
            </Typography>
            {hasUnsavedChanges && (
              <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600 }}>
                Unsaved changes
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {projectRules.map(entry => (
              <Paper key={entry.key} variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography sx={{ fontWeight: 600 }}>{entry.label}</Typography>
                      {entry.justification && (
                        <Tooltip title={entry.justification} arrow>
                          <Box component="span" sx={{ color: 'primary.main', lineHeight: 0 }}>
                            <FontAwesomeIcon icon={faInfoCircle} size="sm" />
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                    <CatalogRuleDetails
                      regulatoryBody={entry.regulatoryBody}
                      justification=""
                      setA={entry.setA}
                      setB={entry.setB}
                    />
                  </Box>
                  {canManage && (
                    <Tooltip
                      title={
                        WHITELIST_PROJECT_RULE_REMOVAL_ENABLED
                          ? `Remove ${entry.label}`
                          : 'Removing catalog rules is not available yet'
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => onRemoveProjectRule(entry.key)}
                          disabled={isSaving || !WHITELIST_PROJECT_RULE_REMOVAL_ENABLED}
                          aria-label={`Remove ${entry.label}`}
                          sx={{ color: 'error.main' }}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </Paper>
            ))}

            {newEntries
              .filter(entry => entry.key.trim())
              .map((entry, index) => (
                <Paper
                  key={`new-${index}-${entry.key}`}
                  variant="outlined"
                  sx={{ p: 1.5, bgcolor: 'background.paper', borderStyle: 'dashed' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography sx={{ fontWeight: 600 }}>{humanizeKey(entry.key)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Custom rule ·{' '}
                        {entry.regulatory_body.length > 0 ? entry.regulatory_body.join(', ') : 'No regulatory body'}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              ))}
          </Box>
        </Paper>
      )}

      {!showCatalog && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No catalog rules for {domainLabel} yet. You can still add custom rules.
          {onRefresh && (
            <Button size="small" sx={{ ml: 1 }} onClick={onRefresh}>
              Refresh
            </Button>
          )}
        </Alert>
      )}

      {showCatalog && (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Available catalog
          </Typography>

          <TextField
            fullWidth
            size="small"
            placeholder="Search available rules..."
            value={searchQuery}
            onChange={event => setSearchQuery(event.target.value)}
            sx={{ mb: 1.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FontAwesomeIcon icon={faMagnifyingGlass} className="text-muted" />
                </InputAdornment>
              )
            }}
          />

          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3, maxHeight: 360, overflowY: 'auto', pr: 0.5 }}
          >
            {filteredAvailableEntries.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                {availableCatalogEntries.length === 0
                  ? 'All catalog rules are on your project whitelist.'
                  : `No rules match "${searchQuery.trim()}".`}
              </Typography>
            ) : (
              filteredAvailableEntries.map(entry => (
                <Paper key={entry.key} variant="outlined" sx={{ p: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography sx={{ fontWeight: 600 }}>{entry.label}</Typography>
                        {entry.justification && (
                          <Tooltip title={entry.justification} arrow>
                            <Box component="span" sx={{ color: 'primary.main', lineHeight: 0 }}>
                              <FontAwesomeIcon icon={faInfoCircle} size="sm" />
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                      <CatalogRuleDetails
                        regulatoryBody={entry.regulatoryBody}
                        justification=""
                        setA={entry.setA}
                        setB={entry.setB}
                      />
                    </Box>
                    {canManage && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onAddCatalogRule(entry.key)}
                        disabled={isSaving}
                      >
                        Add
                      </Button>
                    )}
                  </Box>
                </Paper>
              ))
            )}
          </Box>
        </>
      )}

      {newEntries.length > 0 && (
        <>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
            New custom rules
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            {newEntries.map((entry, index) => (
              <Paper
                key={index}
                variant="outlined"
                sx={{
                  p: 2,
                  borderStyle: 'dashed',
                  borderColor: 'primary.light',
                  bgcolor: 'rgba(13, 110, 253, 0.02)'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                    Custom rule {index + 1}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => onRemoveNewEntry(index)}
                    disabled={!canManage}
                    aria-label="Remove custom rule"
                    sx={{ color: 'error.main' }}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </IconButton>
                </Box>

                <TextField
                  fullWidth
                  label="Rule key"
                  value={entry.key}
                  onChange={event => onNewEntryKeyChange(index, event.target.value)}
                  placeholder="e.g. age, gender, location"
                  size="small"
                  disabled={!canManage}
                  sx={{ mb: 2 }}
                />

                <Typography variant="caption" color="primary" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                  Regulatory body
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <TagInput
                    tags={entry.regulatory_body}
                    onAddTag={value => onAddRegulatoryTag(index, value)}
                    onRemoveTag={tagIndex => onRemoveRegulatoryTag(index, tagIndex)}
                    placeholder="e.g. HIPAA, GDPR, FDA GMLP"
                    disabled={!canManage}
                  />
                </Box>

                <TextField
                  fullWidth
                  label="Justification"
                  value={entry.justification}
                  onChange={event => onNewEntryJustificationChange(index, event.target.value)}
                  placeholder="Why should this attribute be whitelisted for your project?"
                  size="small"
                  multiline
                  minRows={2}
                  disabled={!canManage}
                />
              </Paper>
            ))}
          </Box>
        </>
      )}

      <Divider sx={{ mb: 2 }} />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }}>
        <Button
          variant="outlined"
          startIcon={<FontAwesomeIcon icon={faPlus} />}
          onClick={onAddNewEntry}
          disabled={!canManage || isSaving}
        >
          Add rule
        </Button>
        <Button variant="outlined" onClick={onReset} disabled={!canManage || isSaving}>
          Reset
        </Button>
        {canSave && (
          <Button variant="contained" onClick={onSave} disabled={isSaving || !hasUnsavedChanges}>
            {isSaving ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                Saving...
              </Box>
            ) : (
              'Save changes'
            )}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default WhitelistRulesTab;
